import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { STATUS_FLAGS, EMPTY_STATUSES, STATUS_LABEL_TO_KEY, getActiveStatuses, getPrimaryStatusLabel, getPrimaryStatusClass } from '../utils/statusHelpers'

// Module-level so React never remounts it on parent re-render
function SortHeader({ label, column, sortColumn, sortDirection, onSort, className }) {
  const isActive = sortColumn === column
  return (
    <div
      className={`${className || ''} sortable-header${isActive ? ' sort-active' : ''}`}
      onClick={() => onSort(column)}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="sort-indicator">{isActive ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ' ↕'}</span>
    </div>
  )
}

function MyCollection() {
  const { user } = useAuth()
  const [collection, setCollection] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('list')

  // Filters
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterBrand, setFilterBrand] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterModel, setFilterModel] = useState('')
  const [filterColor, setFilterColor] = useState('')
  const [filterWeight, setFilterWeight] = useState('')
  const [filterLength, setFilterLength] = useState('')
  const [filterDepth, setFilterDepth] = useState('')
  const [filterCollectible, setFilterCollectible] = useState('all')

  // Sort
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [editQuantity, setEditQuantity] = useState(1)
  const [editCondition, setEditCondition] = useState('New')
  const [editNotes, setEditNotes] = useState('')
  const [editPriority, setEditPriority] = useState('Must Have')
  const [editStatuses, setEditStatuses] = useState(EMPTY_STATUSES)
  const [editIsCollectible, setEditIsCollectible] = useState(false)

  const FILTER_STATUSES = ['All', ...STATUS_FLAGS.map(s => s.label)]

  const WISHLIST_PRIORITIES = [
    'Must Have', 'Love to Have', 'Like to Have', 'Thinking About It', "Don't Buy This"
  ]

  const GALLERY_SORT_OPTIONS = [
    { key: 'lure',      label: 'Lure Name' },
    { key: 'brand',     label: 'Brand' },
    { key: 'category',  label: 'Category' },
    { key: 'color',     label: 'Color' },
    { key: 'status',    label: 'Status' },
    { key: 'qty',       label: 'Qty' },
    { key: 'myRating',  label: 'My Rating' },
    { key: 'avgRating', label: 'Avg Rating' },
  ]

  useEffect(() => {
    if (user) fetchCollection()
  }, [user])

  const fetchCollection = async () => {
    try {
      const { data, error } = await supabase
        .from('user_lure_inventory')
        .select(`
          *,
          lure_variants (
            variant_id,
            variant_name,
            is_limited_edition,
            image_url,
            color_name,
            base_lures (
              base_lure_id,
              name,
              weight,
              length,
              depth_range,
              brands (brand_name),
              lure_types (type_name),
              lure_reviews (rating, user_id)
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setCollection(data || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching collection:', error)
      setLoading(false)
    }
  }

  // Derived filter options
  const availableBrands = useMemo(() =>
    [...new Set(collection.map(i => i.lure_variants?.base_lures?.brands?.brand_name).filter(Boolean))].sort()
  , [collection])

  const availableCategories = useMemo(() =>
    [...new Set(collection.map(i => i.lure_variants?.base_lures?.lure_types?.type_name).filter(Boolean))].sort()
  , [collection])

  // Stats from full unfiltered collection
  const stats = useMemo(() => {
    let totalQuantity = 0
    let ownedCount = 0
    const brandSet = new Set()
    collection.forEach(item => {
      totalQuantity += item.quantity || 0
      if (item.status_own) ownedCount++
      if (item.lure_variants?.base_lures?.brands?.brand_name)
        brandSet.add(item.lure_variants.base_lures.brands.brand_name)
    })
    return { total: collection.length, totalQuantity, brands: brandSet.size, ownedCount }
  }, [collection])

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Filter + enrich with ratings + sort
  const filteredCollection = useMemo(() => {
    // 1. Filter
    let result = collection.filter(item => {
      const variant = item.lure_variants
      const baseLure = variant?.base_lures
      const colorLabel = (variant?.variant_name || variant?.color_name || '').toLowerCase()

      if (filterStatus !== 'All' && !item[STATUS_LABEL_TO_KEY[filterStatus]]) return false
      if (filterBrand && baseLure?.brands?.brand_name !== filterBrand) return false
      if (filterCategory && baseLure?.lure_types?.type_name !== filterCategory) return false
      if (filterModel && !baseLure?.name?.toLowerCase().includes(filterModel.toLowerCase())) return false
      if (filterColor && !colorLabel.includes(filterColor.toLowerCase())) return false
      if (filterWeight && !baseLure?.weight?.toLowerCase().includes(filterWeight.toLowerCase())) return false
      if (filterLength && !baseLure?.length?.toLowerCase().includes(filterLength.toLowerCase())) return false
      if (filterDepth && !baseLure?.depth_range?.toLowerCase().includes(filterDepth.toLowerCase())) return false
      if (filterCollectible === 'yes' && !item.is_collectible) return false
      if (filterCollectible === 'no' && item.is_collectible) return false
      return true
    })

    // 2. Enrich with computed ratings
    result = result.map(item => {
      const reviews = item.lure_variants?.base_lures?.lure_reviews || []
      const avgRating = reviews.length > 0
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : null
      const myRating = user
        ? (reviews.find(r => r.user_id === user.id)?.rating ?? null)
        : null
      return { ...item, avgRating, myRating }
    })

    // 3. Sort
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aVal, bVal

        switch (sortColumn) {
          case 'lure':
            aVal = a.lure_variants?.base_lures?.name || ''
            bVal = b.lure_variants?.base_lures?.name || ''
            break
          case 'color':
            aVal = a.lure_variants?.variant_name || a.lure_variants?.color_name || ''
            bVal = b.lure_variants?.variant_name || b.lure_variants?.color_name || ''
            break
          case 'brand':
            aVal = a.lure_variants?.base_lures?.brands?.brand_name || ''
            bVal = b.lure_variants?.base_lures?.brands?.brand_name || ''
            break
          case 'category':
            aVal = a.lure_variants?.base_lures?.lure_types?.type_name || ''
            bVal = b.lure_variants?.base_lures?.lure_types?.type_name || ''
            break
          case 'weight':
            aVal = a.lure_variants?.base_lures?.weight || ''
            bVal = b.lure_variants?.base_lures?.weight || ''
            break
          case 'length':
            aVal = a.lure_variants?.base_lures?.length || ''
            bVal = b.lure_variants?.base_lures?.length || ''
            break
          case 'depth':
            aVal = a.lure_variants?.base_lures?.depth_range || ''
            bVal = b.lure_variants?.base_lures?.depth_range || ''
            break
          case 'status':
            aVal = getPrimaryStatusLabel(a)
            bVal = getPrimaryStatusLabel(b)
            break
          case 'qty':
            aVal = a.quantity ?? 0
            bVal = b.quantity ?? 0
            break
          case 'myRating':
            aVal = a.myRating
            bVal = b.myRating
            break
          case 'avgRating':
            aVal = a.avgRating
            bVal = b.avgRating
            break
          default:
            return 0
        }

        // Nulls always last regardless of direction
        if (aVal === null && bVal === null) return 0
        if (aVal === null) return 1
        if (bVal === null) return -1

        const cmp = typeof aVal === 'string'
          ? aVal.localeCompare(bVal)
          : aVal - bVal

        return sortDirection === 'asc' ? cmp : -cmp
      })
    }

    return result
  }, [collection, filterStatus, filterBrand, filterCategory, filterModel, filterColor,
    filterWeight, filterLength, filterDepth, filterCollectible, sortColumn, sortDirection, user?.id])

  const hasActiveFilters = filterStatus !== 'All' || filterBrand || filterCategory ||
    filterModel || filterColor || filterWeight || filterLength || filterDepth || filterCollectible !== 'all'

  const clearFilters = () => {
    setFilterStatus('All')
    setFilterBrand('')
    setFilterCategory('')
    setFilterModel('')
    setFilterColor('')
    setFilterWeight('')
    setFilterLength('')
    setFilterDepth('')
    setFilterCollectible('all')
  }

  const removeFromCollection = async (inventoryId) => {
    if (!window.confirm('Remove this item from your collection?')) return
    try {
      const { error } = await supabase
        .from('user_lure_inventory')
        .delete()
        .eq('inventory_id', inventoryId)
        .eq('user_id', user.id)
      if (error) throw error
      fetchCollection()
    } catch (error) {
      console.error('Error removing lure:', error)
      alert('Failed to remove lure from collection')
    }
  }

  const openEditModal = (item) => {
    setEditingItem(item)
    setEditQuantity(item.quantity)
    setEditCondition(item.condition || 'New')
    setEditNotes(item.notes || '')
    setEditStatuses(Object.fromEntries(STATUS_FLAGS.map(s => [s.key, item[s.key] || false])))
    setEditIsCollectible(item.is_collectible || false)
    setEditPriority(item.wishlist_priority || 'Must Have')
    setShowEditModal(true)
  }

  const handleUpdateItem = async () => {
    if (!editingItem) return
    try {
      const { error } = await supabase
        .from('user_lure_inventory')
        .update({
          quantity: editQuantity,
          condition: editCondition,
          notes: editNotes || null,
          is_collectible: editIsCollectible,
          wishlist_priority: editStatuses.status_wishlist ? editPriority : null,
          ...editStatuses
        })
        .eq('inventory_id', editingItem.inventory_id)
        .eq('user_id', user.id)
      if (error) throw error
      setShowEditModal(false)
      fetchCollection()
    } catch (error) {
      console.error('Error updating item:', error)
      alert('Failed to update item')
    }
  }

  const formatRating = (r) => r !== null && r !== undefined ? r.toFixed(1) : '—'

  if (loading) return <div className="loading">Loading your collection...</div>

  return (
    <div className="my-collection-page">
      {/* Header */}
      <div className="collection-header">
        <div>
          <h1>My Tackle Box</h1>
          <p className="page-subtitle">Manage your fishing lure collection</p>
        </div>
        <div className="view-toggle">
          <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="List view">
            ☰ List
          </button>
          <button className={`view-toggle-btn ${viewMode === 'gallery' ? 'active' : ''}`} onClick={() => setViewMode('gallery')} title="Gallery view">
            ⊞ Gallery
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="collection-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Entries</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalQuantity}</div>
          <div className="stat-label">Total Pieces</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.brands}</div>
          <div className="stat-label">Brands</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.ownedCount}</div>
          <div className="stat-label">Owned</div>
        </div>
      </div>

      {collection.length === 0 ? (
        <div className="empty-collection">
          <h2>Your collection is empty</h2>
          <p>Start building your tackle box by adding lures from the database!</p>
          <Link to="/browse" className="btn-primary">Browse Lures</Link>
        </div>
      ) : (
        <>
          {/* Filter Panel */}
          <div className="collection-filters">
            <div className="collection-filters-row">
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
                {FILTER_STATUSES.map(s => (
                  <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
                ))}
              </select>
              <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="filter-select">
                <option value="">All Brands</option>
                {availableBrands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="filter-select">
                <option value="">All Categories</option>
                {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="text" value={filterModel} onChange={e => setFilterModel(e.target.value)} className="filter-input" placeholder="Model name..." />
              <input type="text" value={filterColor} onChange={e => setFilterColor(e.target.value)} className="filter-input" placeholder="Color..." />
              <input type="text" value={filterWeight} onChange={e => setFilterWeight(e.target.value)} className="filter-input filter-input-narrow" placeholder="Weight..." />
              <input type="text" value={filterLength} onChange={e => setFilterLength(e.target.value)} className="filter-input filter-input-narrow" placeholder="Length..." />
              <input type="text" value={filterDepth} onChange={e => setFilterDepth(e.target.value)} className="filter-input filter-input-narrow" placeholder="Depth..." />
              <select value={filterCollectible} onChange={e => setFilterCollectible(e.target.value)} className="filter-select">
                <option value="all">All Items</option>
                <option value="yes">Collectible Only</option>
                <option value="no">Fishing Only</option>
              </select>
              {hasActiveFilters && (
                <button className="btn-secondary" onClick={clearFilters}>Clear</button>
              )}
            </div>
            <div className="collection-results-count">
              Showing {filteredCollection.length} of {collection.length} items
              {hasActiveFilters && ' (filtered)'}
              {sortColumn && (
                <span>
                  {' · Sorted by '}<strong>{sortColumn}</strong>{' '}{sortDirection === 'asc' ? '▲' : '▼'}
                  {' '}<button className="clear-sort-btn" onClick={() => setSortColumn(null)}>✕</button>
                </span>
              )}
            </div>
          </div>

          {filteredCollection.length === 0 ? (
            <div className="no-results-filter">
              <p>No items match your filters.</p>
              <button onClick={clearFilters} className="btn-secondary">Clear Filters</button>
            </div>
          ) : viewMode === 'list' ? (
            /* ─── LIST VIEW ─── */
            <div className="collection-list">
              <div className="collection-list-header">
                <div className="col-img"></div>
                <SortHeader label="Lure"     column="lure"      sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-lure" />
                <SortHeader label="Color"    column="color"     sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-color" />
                <SortHeader label="Brand"    column="brand"     sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-brand" />
                <SortHeader label="Category" column="category"  sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-type" />
                <SortHeader label="Status"   column="status"    sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-status" />
                <div className="col-collectible sortable-header">Collectible</div>
                <SortHeader label="Qty"      column="qty"       sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-qty" />
                <SortHeader label="My ★"     column="myRating"  sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-rating" />
                <SortHeader label="Avg ★"    column="avgRating" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} className="col-rating" />
                <div className="col-actions"></div>
              </div>

              {filteredCollection.map(item => {
                const variant = item.lure_variants
                const baseLure = variant?.base_lures
                const colorLabel = variant?.variant_name || variant?.color_name || '—'

                return (
                  <div key={item.inventory_id} className="collection-list-row">
                    <div className="col-img">
                      {variant?.image_url
                        ? <img src={variant.image_url} alt={colorLabel} className="list-thumb" />
                        : <div className="list-thumb-placeholder">🎣</div>
                      }
                    </div>
                    <div className="col-lure">
                      <Link to={`/lure/${baseLure?.base_lure_id}`} className="list-lure-name">
                        {baseLure?.name || '—'}
                      </Link>
                      {variant?.is_limited_edition && <span className="le-badge">LE</span>}
                      {item.condition && item.condition !== 'New' && (
                        <span className="list-condition">{item.condition}</span>
                      )}
                    </div>
                    <div className="col-color">{colorLabel}</div>
                    <div className="col-brand">{baseLure?.brands?.brand_name || '—'}</div>
                    <div className="col-type">{baseLure?.lure_types?.type_name || '—'}</div>
                    <div className="col-status">
                      <div className="multi-status-pills">
                        {getActiveStatuses(item).map(s => (
                          <span key={s.key} className={`collection-status-pill ${s.cssClass}`}>{s.label}</span>
                        ))}
                      </div>
                    </div>
                    <div className="col-collectible">
                      {item.is_collectible && <span className="collection-status-pill status-collectible">Yes</span>}
                    </div>
                    <div className="col-qty">{item.quantity}</div>
                    <div className={`col-rating${item.myRating !== null ? ' rating-my' : ' rating-empty'}`}>
                      {item.myRating !== null ? item.myRating : '—'}
                    </div>
                    <div className={`col-rating${item.avgRating !== null ? ' rating-avg' : ' rating-empty'}`}>
                      {item.avgRating !== null ? formatRating(item.avgRating) : '—'}
                    </div>
                    <div className="col-actions">
                      <button onClick={() => openEditModal(item)} className="list-action-btn" title="Edit">✏️</button>
                      <button onClick={() => removeFromCollection(item.inventory_id)} className="list-action-btn list-action-remove" title="Remove">✕</button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            /* ─── GALLERY VIEW ─── */
            <>
              <div className="hitlist-sort-bar" style={{ marginTop: 0, marginBottom: '1rem' }}>
                <span className="sort-label">Sort:</span>
                {GALLERY_SORT_OPTIONS.map(({ key, label }) => (
                  <button
                    key={key}
                    className={`sort-btn${sortColumn === key ? ' active' : ''}`}
                    onClick={() => handleSort(key)}
                  >
                    {label}{sortColumn === key && <span> {sortDirection === 'asc' ? '▲' : '▼'}</span>}
                  </button>
                ))}
                {sortColumn && (
                  <button className="sort-btn" onClick={() => setSortColumn(null)}>✕ Clear</button>
                )}
              </div>

              <div className="collection-gallery">
                {filteredCollection.map(item => {
                  const variant = item.lure_variants
                  const baseLure = variant?.base_lures
                  const colorLabel = variant?.variant_name || variant?.color_name || ''

                  return (
                    <div key={item.inventory_id} className="gallery-card">
                      <div className="gallery-image-wrap">
                        {variant?.image_url ? (
                          <img src={variant.image_url} alt={colorLabel} className="gallery-image" />
                        ) : (
                          <div className="gallery-placeholder">🎣</div>
                        )}
                        <span className={`gallery-status-badge ${getPrimaryStatusClass(item)}`}>
                          {getPrimaryStatusLabel(item) || 'No Status'}
                          {getActiveStatuses(item).length > 1 && ` +${getActiveStatuses(item).length - 1}`}
                        </span>
                      </div>
                      <div className="gallery-info">
                        <Link to={`/lure/${baseLure?.base_lure_id}`} className="gallery-lure-name">
                          {baseLure?.name}
                        </Link>
                        <div className="gallery-color">
                          {colorLabel}
                          {variant?.is_limited_edition && <span className="le-badge">LE</span>}
                        </div>
                        <div className="gallery-meta">
                          {baseLure?.brands?.brand_name}
                          {item.is_collectible && (
                            <span className="collection-status-pill status-collectible" style={{ marginLeft: '0.4rem', fontSize: '0.65rem', verticalAlign: 'middle' }}>Collectible</span>
                          )}
                        </div>
                        <div className="gallery-ratings">
                          {item.myRating !== null && (
                            <span className="gallery-my-rating" title="My rating">★ {item.myRating}</span>
                          )}
                          {item.avgRating !== null && (
                            <span className="gallery-avg-rating" title="Site average">avg {formatRating(item.avgRating)}</span>
                          )}
                        </div>
                      </div>
                      <div className="gallery-actions">
                        <button onClick={() => openEditModal(item)} className="btn-secondary btn-sm">✏️ Edit</button>
                        <button onClick={() => removeFromCollection(item.inventory_id)} className="list-action-btn list-action-remove" title="Remove">✕</button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Item</h2>
              <button onClick={() => setShowEditModal(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <p className="modal-variant-name">
                {editingItem.lure_variants?.base_lures?.name} —{' '}
                {editingItem.lure_variants?.variant_name || editingItem.lure_variants?.color_name}
              </p>
              <div className="form-group">
                <label>Collection Status (select all that apply)</label>
                <div className="status-checkbox-group">
                  {STATUS_FLAGS.map(s => (
                    <label key={s.key} className="status-checkbox-label">
                      <input
                        type="checkbox"
                        checked={editStatuses[s.key] || false}
                        onChange={e => setEditStatuses(prev => ({ ...prev, [s.key]: e.target.checked }))}
                      />
                      <span className={`collection-status-pill ${s.cssClass}`}>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {editStatuses.status_wishlist && (
                <div className="form-group">
                  <label>Wishlist Priority</label>
                  <select value={editPriority} onChange={e => setEditPriority(e.target.value)} className="form-select">
                    {WISHLIST_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="status-checkbox-label">
                  <input
                    type="checkbox"
                    checked={editIsCollectible}
                    onChange={e => setEditIsCollectible(e.target.checked)}
                  />
                  <span>Collectible only (not for fishing)</span>
                </label>
              </div>
              <div className="form-group">
                <label>Quantity *</label>
                <input type="number" min="1" value={editQuantity} onChange={e => setEditQuantity(parseInt(e.target.value))} className="form-input" />
              </div>
              <div className="form-group">
                <label>Condition</label>
                <select value={editCondition} onChange={e => setEditCondition(e.target.value)} className="form-select">
                  <option>New</option>
                  <option>Like New</option>
                  <option>Very Good</option>
                  <option>Good</option>
                  <option>Acceptable</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} className="form-textarea" rows="3" placeholder="Add any notes about this item..." />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowEditModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleUpdateItem} className="btn-primary">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyCollection
